/**
 * TransferReportsSearchStep Container Component
 *
 * Manages debounced player search, client-side isManual filtering,
 * and cursor-based pagination. Exposes a custom hook for the search
 * logic and renders the search step view.
 * @module features/player-detail/components/TransferReportsSearchStep
 */

import { NetworkStatus } from "@apollo/client";
import { type ListRenderItem } from "@/components/atoms/FlashList";
import { useCallback, useMemo, useState } from "react";
import { Spinner } from "@/components/atoms/Spinner";
import { useDebouncedCallback } from "use-debounce";

import type { TransferTargetPlayer } from "@/features/player-detail/utils/transferReportsTypes";
import { toTransferTargetPlayer } from "@/features/player-detail/utils/transferReportsTypes";
import SearchResultRow from "./SearchResultRow";
import TransferReportsSearchStepView from "./TransferReportsSearchStepView";

import { Text } from "@/components/atoms/Text";
import { Stack } from "@/components/atoms/Stack";
import type { PlayerSearchPlayerFragment } from "@/generated/graphql";
import { usePlayerSearchSearchPlayersLiteQuery } from "@/generated/graphql";

/** Minimum character count before firing the search query */
const MIN_QUERY_LENGTH = 2;

/** Page size for search results */
const PAGE_SIZE = 20;

/**
 * Custom hook encapsulating the debounced search, filtering, and pagination
 * logic for the target player search step.
 */
export const useTransferReportsSearch = () => {
  const [searchValue, setSearchValue] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const handleDebounce = useDebouncedCallback(setDebouncedQuery, 300);

  const shouldSkip = debouncedQuery.length < MIN_QUERY_LENGTH;

  const { data, loading, fetchMore, networkStatus } =
    usePlayerSearchSearchPlayersLiteQuery({
      variables: { query: debouncedQuery, first: PAGE_SIZE },
      skip: shouldSkip,
      notifyOnNetworkStatusChange: true,
    });

  const isFetchingMore = networkStatus === NetworkStatus.fetchMore;

  const players: readonly PlayerSearchPlayerFragment[] = useMemo(
    () =>
      data?.searchPlayers?.edges
        ?.map(edge => edge.node)
        .filter(node => !node.isManual) ?? [],
    [data?.searchPlayers?.edges]
  );

  const pageInfo = useMemo(
    () => data?.searchPlayers?.pageInfo,
    [data?.searchPlayers?.pageInfo]
  );

  const handleLoadMore = useCallback(() => {
    const { hasNextPage, endCursor } = pageInfo ?? {};
    if (hasNextPage && !isFetchingMore) {
      fetchMore({ variables: { after: endCursor } });
    }
  }, [fetchMore, isFetchingMore, pageInfo]);

  const handleSearchChange = useCallback(
    (text: string) => {
      setSearchValue(text);
      handleDebounce(text);
    },
    [handleDebounce]
  );

  const handleClear = useCallback(() => {
    setSearchValue("");
    setDebouncedQuery("");
    handleDebounce.cancel();
  }, [handleDebounce]);

  return {
    searchValue,
    players,
    loading,
    handleLoadMore,
    handleSearchChange,
    handleClear,
  };
};

/** Props for TransferReportsSearchStepContainer */
export interface TransferReportsSearchStepContainerProps {
  /** Whether dark mode is active */
  readonly isDark: boolean;
  /** Callback when a target player is selected */
  readonly onSelectPlayer: (player: TransferTargetPlayer) => void;
}

/**
 * Container component for the target player search step.
 * Manages search logic, render callbacks, and player selection.
 * @param root0 - Component props
 * @param root0.isDark - Whether dark mode is active
 * @param root0.onSelectPlayer - Callback fired when user taps a search result
 */
const TransferReportsSearchStepContainer = ({
  isDark,
  onSelectPlayer,
}: TransferReportsSearchStepContainerProps) => {
  const {
    searchValue,
    players,
    loading,
    handleLoadMore,
    handleSearchChange,
    handleClear,
  } = useTransferReportsSearch();

  const handlePlayerPress = useCallback(
    (player: PlayerSearchPlayerFragment) => {
      onSelectPlayer(toTransferTargetPlayer(player));
    },
    [onSelectPlayer]
  );

  const renderItem: ListRenderItem<PlayerSearchPlayerFragment> = useCallback(
    ({ item }) => (
      <SearchResultRow
        player={item}
        isDark={isDark}
        onPlayerPress={handlePlayerPress}
      />
    ),
    [isDark, handlePlayerPress]
  );

  const renderEmpty = useCallback(
    () =>
      loading ? (
        <Stack className="flex-1 items-center justify-center gap-3 py-12">
          <Spinner size="large" />
          <Text variant="body" className="text-content-secondary">
            Searching players...
          </Text>
        </Stack>
      ) : (
        <Stack className="flex-1 items-center justify-center gap-1 py-12">
          <Text
            variant="title-sm"
            className="text-center text-content-secondary"
          >
            Search for a player to transfer data to
          </Text>
          <Text variant="body" className="text-center text-content-muted">
            Only provider-sourced players are shown
          </Text>
        </Stack>
      ),
    [loading]
  );

  return (
    <TransferReportsSearchStepView
      searchValue={searchValue}
      players={players}
      isDark={isDark}
      onSearchChange={handleSearchChange}
      onClear={handleClear}
      onLoadMore={handleLoadMore}
      renderItem={renderItem}
      renderEmpty={renderEmpty}
    />
  );
};

TransferReportsSearchStepContainer.displayName =
  "TransferReportsSearchStepContainer";

export default TransferReportsSearchStepContainer;
